import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { VictoryPie } from "victory";
import { useAuth } from "../../../../context/AuthContext";
import { useTranslation } from "react-i18next";
import "../../User/SubPages/UserSubject.css";
import { API_BASE_URL } from "../../../../Helpers/apiConfig";

const TAGS = {
    1: { name: "Listening / Dinleme", color: "#C85AC9" },
    2: { name: "Writing / Yazma", color: "#9DC97B" },
    4: { name: "Reading / Okuma", color: "#FF7F27" },
    5: { name: "Speaking / Konuşma", color: "#5DE5F5" },
};

const EXCLUDED_TAG_ID = 3;

function pct(correct, total) {
    if (!total) return 0;
    return Math.round((correct / total) * 100);
}

function Bar({ rate, color }) {
    return (
        <div className="progress modern-progress">
            <div
                className="progress-bar modern-progress-bar"
                style={{
                    width: `${rate}%`,
                    backgroundColor: color,
                }}
            >
                %{rate}
            </div>
        </div>
    );
}

function ManagerStudentReport() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const navigate = useNavigate();
    const { id: studentId } = useParams();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Rapor ve Öğrenci Bilgisi State'leri
    const [reportData, setReportData] = useState([]);
    const [studentInfo, setStudentInfo] = useState(null);
    const [selectedCourseId, setSelectedCourseId] = useState("");

    useEffect(() => {
        if (studentId) {
            fetchData();
        }
    }, [studentId]);

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const headers = {
                accept: "*/*",
                "Content-Type": "application/json",
                Authorization: "Bearer " + user.token,
            };

            // 1. Öğrenci Genel Bilgilerini Getir
            const studentRes = await axios.get(
                `${API_BASE_URL}/manager/EditSchool/GetStudentById`,
                { headers, params: { studentId } }
            );
            setStudentInfo(studentRes.data?.data);

            // 2. Rapor Verilerini Getir
            const reportRes = await axios.get(
                `${API_BASE_URL}/manager/EditSchool/GetReportById`,
                { headers, params: { studentId } }
            );

            const data = reportRes.data?.data || [];
            setReportData(data);

            if (data.length > 0) {
                setSelectedCourseId(data[0].courseId);
            }
        } catch (err) {
            setError("Veriler yüklenirken bir hata oluştu.");
        } finally {
            setLoading(false);
        }
    };

    const toPlainOptionLabel = (str = "") =>
        str.replace(/<\s*sup\s*>/gi, "").replace(/<\s*\/\s*sup\s*>/gi, "");

    const availableCourses = useMemo(() => {
        return reportData.map(c => ({
            id: c.courseId,
            name: c.courseName,
            desc: c.courseDesc || ""
        }));
    }, [reportData]);

    const selectedCourseData = useMemo(() => {
        return reportData.find(c => c.courseId === selectedCourseId) || null;
    }, [reportData, selectedCourseId]);

    const generalStats = useMemo(() => {
        let total = 0;
        let correct = 0;
        const skills = {
            1: { total: 0, correct: 0, rate: 0 },
            2: { total: 0, correct: 0, rate: 0 },
            4: { total: 0, correct: 0, rate: 0 },
            5: { total: 0, correct: 0, rate: 0 },
        };

        if (selectedCourseData) {
            selectedCourseData.subjects.forEach(subject => {
                subject.answers.forEach(answer => {
                    if (answer.isDeleted || answer.tagId === EXCLUDED_TAG_ID) return;
                    total++;
                    if (answer.isTrue) correct++;
                    if (skills[answer.tagId]) {
                        skills[answer.tagId].total++;
                        if (answer.isTrue) skills[answer.tagId].correct++;
                    }
                });
            });
        }

        Object.keys(skills).forEach(tagId => {
            skills[tagId].rate = pct(skills[tagId].correct, skills[tagId].total);
        });

        return {
            total, correct,
            wrong: total - correct,
            successRate: pct(correct, total),
            skills,
        };
    }, [selectedCourseData]);

    const subjectsStats = useMemo(() => {
        if (!selectedCourseData) return [];
        const subjectsMap = {};

        selectedCourseData.subjects.forEach((subj) => {
            if (!subjectsMap[subj.subjectId]) {
                subjectsMap[subj.subjectId] = {
                    subjectId: subj.subjectId,
                    subjectName: subj.subjectName,
                    total: 0, correct: 0, wrong: 0, rate: 0,
                    tags: {
                        1: { total: 0, correct: 0, wrong: 0, rate: 0 },
                        2: { total: 0, correct: 0, wrong: 0, rate: 0 },
                        4: { total: 0, correct: 0, wrong: 0, rate: 0 },
                        5: { total: 0, correct: 0, wrong: 0, rate: 0 },
                    },
                };
            }

            subj.answers.forEach((a) => {
                if (a.isDeleted || a.tagId === EXCLUDED_TAG_ID) return;
                subjectsMap[subj.subjectId].total += 1;
                if (a.isTrue) subjectsMap[subj.subjectId].correct += 1;
                if (subjectsMap[subj.subjectId].tags[a.tagId]) {
                    subjectsMap[subj.subjectId].tags[a.tagId].total += 1;
                    if (a.isTrue) subjectsMap[subj.subjectId].tags[a.tagId].correct += 1;
                }
            });
        });

        Object.values(subjectsMap).forEach((s) => {
            s.wrong = s.total - s.correct;
            s.rate = pct(s.correct, s.total);
            Object.keys(s.tags).forEach((tagId) => {
                const t = s.tags[tagId];
                t.wrong = t.total - t.correct;
                t.rate = pct(t.correct, t.total);
            });
        });

        return Object.values(subjectsMap).sort((a, b) => a.subjectId - b.subjectId);
    }, [selectedCourseData]);

    const SkillRow = ({ title, color, stat }) => (
        <div className="mb-4">
            <div className="d-flex justify-content-between mb-1">
                <strong>{title}</strong>
                <small className="text-muted">
                    {stat.total} soru · {stat.correct} d · {stat.total - stat.correct} y
                </small>
            </div>
            <div className="progress" style={{ height: 22, backgroundColor: "#e9ecef", borderRadius: "8px" }}>
                <div
                    className="progress-bar"
                    style={{ width: `${stat.rate}%`, backgroundColor: color, fontWeight: "bold", borderRadius: "8px" }}
                >
                    %{stat.rate}
                </div>
            </div>
        </div>
    );

    if (loading) return <div className="p-4 text-center">Yükleniyor...</div>;
    if (error) return <div className="p-4 text-center text-danger">{error}</div>;

    return (
        <div className="container-fluid mt-4">
            {/* Üst Kısım: Öğrenci Bilgileri ve Geri Dön Butonu */}
            <div className="row mb-3">
                <div className="col-12">
                    <div className="card shadow-sm border-0 bg-light">
                        <div className="card-body d-flex justify-content-between align-items-center">
                            <div>
                                <h4 className="mb-0 text-primary">{studentInfo?.fullName}</h4>
                                <span className="text-muted small">{studentInfo?.username}</span>
                            </div>
                            <button
                                className="btn btn-outline-secondary btn-sm rounded-pill px-4"
                                onClick={() => navigate(`/school/studentlist/${studentInfo?.classId}`)}
                            >
                                <i className="bi bi-arrow-left me-2"></i>
                                Sınıf Listesine Geri Dön
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Kurs Seçici */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card shadow-sm">
                        <div className="card-body">
                            <label className="form-label"><strong>Kurs Raporu Filtrele</strong></label>
                            <select
                                className="form-select"
                                value={selectedCourseId}
                                onChange={(e) => setSelectedCourseId(Number(e.target.value))}
                            >
                                {availableCourses.map((course) => (
                                    <option key={course.id} value={course.id}>
                                        {toPlainOptionLabel(`${course.name} ${course.desc ? `- ${course.desc}` : ""}`)}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row g-4">
                {/* SOL TARAF: Genel Rapor */}
                <div className="col-lg-6">
                    <div className="card shadow-sm mb-4">
                        <div className="card-header bg-white text-center py-3">
                            <h5 className="mb-0">Genel İstatistikler</h5>
                            <small className="text-muted">
                                {generalStats.total} soru · {generalStats.correct} d · {generalStats.wrong} y
                            </small>
                        </div>
                        <div className="card-body">
                            <div className="position-relative text-center mb-4">
                                <VictoryPie
                                    data={[
                                        { x: "Doğru", y: generalStats.correct },
                                        { x: "Yanlış", y: generalStats.wrong },
                                    ]}
                                    innerRadius={65}
                                    padding={10}
                                    labels={() => null}
                                    colorScale={["#0d6efd", "#e9ecef"]}
                                    width={200}
                                    height={200}
                                />
                                <div
                                    style={{
                                        position: "absolute",
                                        top: "50%",
                                        left: "50%",
                                        transform: "translate(-50%, -50%)",
                                        textAlign: "center",
                                    }}
                                >
                                    <h3 className="mb-0">% {generalStats.successRate}</h3>
                                    <small className="text-muted">Başarı</small>
                                </div>
                            </div>
                            <div className="px-3">
                                <SkillRow title="Reading / Okuma" color="#FF7F27" stat={generalStats.skills[4]} />
                                <SkillRow title="Writing / Yazma" color="#9DC97B" stat={generalStats.skills[2]} />
                                <SkillRow title="Listening / Dinleme" color="#C85AC9" stat={generalStats.skills[1]} />
                                <SkillRow title="Speaking / Konuşma" color="#5DE5F5" stat={generalStats.skills[5]} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* SAĞ TARAF: Detaylı Analiz */}
                <div className="col-lg-6">
                    <div className="card shadow-sm">
                        <div className="card-header bg-white py-3">
                            <h5 className="mb-0">Konu Bazlı Detaylı Analiz</h5>
                        </div>
                        <div className="card-body" style={{ maxHeight: '750px', overflowY: 'auto' }}>
                            {subjectsStats.map((s) => {
                                const accordionId = `subj_${s.subjectId}`;
                                return (
                                    <div key={s.subjectId} className="mb-4">
                                        <div className="border rounded p-3 modern-subject-card">
                                            <div className="d-flex justify-content-between flex-wrap">
                                                <h5 className="mb-1" dangerouslySetInnerHTML={{ __html: s.subjectName }} />
                                                <div className="text-end">
                                                    <strong>% {s.rate} Başarı</strong>
                                                    <div className="small text-muted">{s.total} Soru</div>
                                                </div>
                                            </div>
                                            <div className="mt-2">
                                                <Bar rate={s.rate} color={s.rate >= 60 ? "#198754" : "#ffc107"} />
                                            </div>
                                            <div className="text-center mt-3">
                                                <button
                                                    className="btn btn-sm btn-outline-primary rounded-pill"
                                                    type="button"
                                                    data-bs-toggle="collapse"
                                                    data-bs-target={`#${accordionId}`}
                                                >
                                                    Beceri Kırılımı
                                                </button>
                                            </div>
                                            <div id={accordionId} className="collapse mt-3">
                                                <div className="row g-2">
                                                    {Object.keys(TAGS).map((tagId) => (
                                                        <div className="col-md-6" key={tagId}>
                                                            <div className="p-2 border rounded bg-white">
                                                                <small className="fw-bold d-block">{TAGS[tagId].name}</small>
                                                                <small className="text-muted d-block mb-1">{s.tags[tagId].correct} D / {s.tags[tagId].total} Soru</small>
                                                                <Bar rate={s.tags[tagId].rate} color={TAGS[tagId].color} />
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ManagerStudentReport;